import { App } from 'App'
import { isEqual } from 'lodash'
import { createRoot } from 'react-dom/client'

import { getCurrentPage } from '@subaio/components'
import { type ApiOptions, apiCreate, delay, initErrorClient, routerCreate } from '@subaio/utils'
import { ContainerStatus, getRoutes } from '@subaio/webapp/src/containers'
import { getContainers } from '@subaio/webapp/src/containers/containers'

import { config } from 'clientConfig'

import type { Container } from 'lib/container'
import { expireSession, storeCreate } from 'lib/store'
import { getUserAuth } from 'lib/userAuth'

initErrorClient(
	{
		enabled: true,
		dataSourceName: config.sentry.dataSourceName,
		release: config.version,
		environment: config.environment,
	},
	() => {
		const {
			page: { name, params, query },
		} = store.getState()

		return {
			user: undefined,
			view: { name, params, query },
		}
	},
)

const store = storeCreate()

if (config.isDev) {
	;(window as any).store = store
}

const apiShouldRetry: ApiOptions['shouldRetry'] = async (error, attempts) => {
	// If the request failed because of a network problem, retry (max 3 times)
	if (error.request.statusCode === 0) {
		// Wait one second longer after each attempt
		await delay(attempts)
		return true
	}

	return false
}

const api = apiCreate({
	baseUrl: '/api',
	getLocation: () => store.getState().page.name,
	getToken: ({ url, method }) => {
		if (method === 'POST' && (url === '/api/users/login' || url === '/api/users/one-time-login')) return null
		const { userAuth } = store.getState()
		if (
			userAuth.status === 'expired' ||
			(userAuth.status === 'authenticated' && getUserAuth(userAuth.user).status !== 'authenticated')
		) {
			if (userAuth.status === 'authenticated') expireSession(store)
			throw new Error('Session expired')
		}
		return userAuth.status === 'authenticated' ? userAuth.user.token : null
	},
	onUnauthorized: (token) => {
		const { userAuth } = store.getState()
		if (userAuth.status === 'authenticated' && userAuth.user.token === token) {
			expireSession(store)
		}
	},
	logRequestData: config.isDev,
	shouldRetry: apiShouldRetry,
})

const root = createRoot(document.getElementById('root')!)

const render = () => {
	const containers = getContainers<Container>()
	const router = routerCreate(getRoutes(containers))
	const currentPage = getCurrentPage(router)
	const { page, status } = store.getState()
	if (currentPage && (!isEqual(page, currentPage) || status === ContainerStatus.Errored)) {
		store.set({ page: currentPage, status: ContainerStatus.Initializing, data: {} })
	}
	// Render app
	root.render(
		<App
			containers={containers}
			store={store}
			router={router}
			api={api}
			setUser={(options) => store.set({ userAuth: getUserAuth(options) })}
		/>,
	)
}

render()

// Hot Module Replacement API
if (module.hot) {
	module.hot.accept(['App', '@subaio/webapp/src/containers/containers'], () => {
		render()
	})
}
